/**
 * useVoiceInput — spoken-input controller for the practice composer (Phase B).
 *
 * Wires the platform STT engine to the composer: interim transcript fills the
 * input, and on end-of-utterance it auto-sends after a short review window (design
 * §7.1 "auto-send by default; review as an option"). Editing cancels the pending
 * auto-send. Preserves unsent speech locally until send; nothing is persisted.
 *
 * Supported only where a confirmed on-device STT path exists (web WASM Whisper /
 * native system recogniser) and voice is enabled. Elsewhere `supported` is false
 * and the caller hides the mic (fail closed).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  STT_LABEL,
  STT_OWNS_ENDPOINTING,
  STT_SUPPORTED,
  onSttPrepareProgress,
  prewarmStt,
  probeSttAssets,
  releaseStt,
  startStt,
  type SttHandle,
} from "./stt";
import { probe } from "@/lib/timing-probe";
import { isCoachPlaying, subscribePlayback } from "./coach-playback";
import { getEndpointConfig } from "./endpoint-config";
import {
  endpointMetrics,
  logEndpointSnapshot,
  type EndpointReason,
} from "./endpoint-metrics";
import { isManualReplayActive, subscribeManualReplay } from "./manual-replay";
import { isUserTyping, subscribeTyping } from "./typing-lock";

// Per-platform end-of-utterance timing. Only consulted when the engine doesn't
// own endpointing (native); the web worker VAD ends turns itself.
const ENDPOINT = getEndpointConfig();

export interface UseVoiceInputOptions {
  /**
   * The live (interim or final) transcript for display. Voice owns its own text
   * channel now — the screen shows this in a transient "listening" bubble, NOT
   * the typed composer, so a typed draft is never clobbered.
   */
  onText: (text: string) => void;
  /** Auto-send the captured transcript directly (independent of the composer). */
  onSend: (text: string) => void;
  /** Review window before auto-send (ms). */
  reviewMs?: number;
  /**
   * End-of-utterance detection: after this many ms of no new speech (a natural
   * "done talking" pause), stop the mic → which triggers the auto-send. Left
   * generous so ordinary thinking pauses don't cut the user off. When unset the
   * mic stays open until `stop()` (the legacy tap-to-stop behaviour).
   */
  silenceMs?: number;
  /**
   * "Review each turn" mode (design §5.1/§7): when true, the transcript is left
   * in the composer for the user to edit and send manually — no auto-send.
   * Default (false) auto-sends after a short pause.
   */
  reviewMode?: boolean;
  /**
   * Called whenever a listening session ends. Lets a hands-free loop resume
   * listening after an empty/aborted session, or bail out (a captured session is
   * handled by the auto-send → reply → replay path).
   *
   * `started` is the one that distinguishes "the user said nothing" from "the
   * mic never opened". Both end with `captured: false`, but the first is normal
   * and should simply listen again, while the second means the engine is failing
   * — and retrying THAT on a fixed delay is what produces an endless
   * getting-ready/ready flicker instead of a working microphone.
   */
  onSessionEnd?: (result: SttSessionEnd) => void;
  /**
   * When true, keep the mic warm ahead of the first turn (acquire it once and
   * reuse it, or pre-clear the permission handshake) so listening starts with no
   * getUserMedia/permission delay. Set it to voice-mode-on; releasing on false
   * frees the mic. Where the platform owns the mic (cloud web) it is a no-op.
   */
  warm?: boolean;
}

export interface SttSessionEnd {
  /** Whether any speech was transcribed this session. */
  captured: boolean;
  /** Whether the engine ever reached live capture. */
  started: boolean;
  /** The STT error code, when the session ended in failure. */
  errorCode?: string;
}

export interface VoiceInput {
  supported: boolean;
  /** How this platform's STT runs (e.g. "on-device (local model)" / "system voice"). */
  label: string;
  /** True once the mic is TRULY capturing (safe to prompt "speak now"). */
  listening: boolean;
  /**
   * True from tapping the mic until capture actually begins — the start-up gap.
   * Show a brief "getting the mic ready…" here instead of "speak now", so the
   * user waits the beat and their first words aren't lost.
   */
  preparing: boolean;
  /**
   * Model-download percentage while `preparing` covers a first-visit model
   * fetch (web only; null otherwise). Lets the UI say WHY the mic is not
   * ready yet instead of leaving a ~77 MB download behind a vague phrase.
   */
  preparingProgress: number | null;
  /**
   * Live mic input level (RMS 0..1) while capturing, for a "you're being heard"
   * meter. 0 when not listening. Only the on-device web engine reports it.
   */
  level: number;
  /** True while the post-utterance auto-send countdown is running. */
  pendingSend: boolean;
  /** A short user-facing message when STT couldn't run (else null). */
  notice: string | null;
  start: () => void;
  stop: () => void;
  /** Cancel a pending auto-send (call when the user edits the transcript). */
  cancelPending: () => void;
  /**
   * Show (or clear) the user-facing notice.
   *
   * For the cases this hook cannot judge on its own. A single failed session is
   * deliberately silent — see `noticeForError` — but the hands-free loop can see
   * a RUN of them, which does deserve saying out loud.
   */
  showNotice: (message: string | null) => void;
}

/**
 * Turn an STT error code into a short, user-facing notice — but ONLY for
 * problems the user can act on. Benign/transient codes (no-speech, aborted,
 * client-side blips, recognizer-busy) stay silent so the mic never nags after
 * a normal tap; the user can simply speak again or type.
 */
function noticeForError(message: string): string | null {
  switch (message) {
    case "permission_denied":
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone access is needed to speak your reply.";
    case "network":
    case "network-timeout":
      return "Speech needs a connection right now — you can type instead.";
    case "audio-capture":
      return "Couldn't reach the microphone — check it's free and try again.";
    default:
      // no-speech, aborted, client, recognizer-busy, unknowns → not worth a nag.
      return null;
  }
}

export function useVoiceInput(opts: UseVoiceInputOptions): VoiceInput {
  // Fail closed until the platform's async capability check lands: on web the
  // sync gate only proves browser primitives exist, and the deployed host can
  // carry the app without the model assets (it did — the mic appeared and then
  // 404'd on first tap). Native's probe resolves in a microtask, so this is one
  // hidden render there, not a flash.
  const [supported, setSupported] = useState(false);
  useEffect(() => {
    let alive = true;
    void probeSttAssets().then((ok) => {
      if (alive) setSupported(ok);
    });
    return () => {
      alive = false;
    };
  }, []);
  const [listening, setListening] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [preparingProgress, setPreparingProgress] = useState<number | null>(null);
  /** When this turn's `start()` was called — the numerator of `voice.mic.live`. */
  const startRequestedAtRef = useRef(0);
  /** Turns whose mic actually opened, so the FIRST one can be told apart. */
  const micTurnsRef = useRef(0);
  const [level, setLevel] = useState(0);
  const lastLevelAt = useRef(0);
  const [pendingSend, setPendingSend] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const handleRef = useRef<SttHandle | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // End-of-utterance timer: reset on each word, fires stop() after a pause.
  const silenceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether this listening session ever captured non-empty speech. Guards
  // against a phantom auto-send when recognition ends with nothing — e.g. an
  // immediate error (Android emits `error` then `end`) or an initial-silence
  // timeout. Without this, `onEnd` would send whatever was already in the box.
  const capturedRef = useRef(false);
  // Latest non-empty transcript this session — what onSend fires with. Voice
  // sends this directly, so it never has to round-trip through the composer.
  const latestTextRef = useRef("");
  // Acoustic endpointing state (native, where the engine keeps the session open
  // and WE decide when a turn ends). `haveLevels` gates the whole thing: only
  // once real `volumechange` audio flows do we trust the acoustic detector;
  // until then the legacy result-gap timer is the fallback. `noiseFloor` adapts
  // to the room; `lastVoiceAt` is the last real-audio / transcript-change tick
  // (what holds the mic open); `lastTextChangeAt` gates transcript-stability.
  const haveLevelsRef = useRef(false);
  const noiseFloorRef = useRef(0);
  const lastVoiceAtRef = useRef(0);
  const lastTextChangeAtRef = useRef(0);
  // Latches once the acoustic detector has ended the turn, so the ~100ms volume
  // ticks that keep arriving before `onEnd` don't call stop() over and over.
  const endingRef = useRef(false);
  // Endpoint-quality instrumentation for this turn (see endpoint-metrics): when
  // capture went live, when the first word landed, and how/with-how-much-silence
  // the turn ended — recorded at auto-send so we can measure premature cut-offs.
  // Whether the engine ever reached live capture this session — see
  // `SttSessionEnd.started`. Distinct from `captureStartedAtRef`, which is a
  // timestamp used for latency metrics.
  const startedRef = useRef(false);
  const captureStartedAtRef = useRef(0);
  const firstCapturedAtRef = useRef(0);
  const endReasonRef = useRef<EndpointReason | null>(null);
  const endTrailingSilenceRef = useRef(0);
  // Fence token. Each listening session captures the generation it started in;
  // the hook's stop() bumps it, invalidating every in-flight callback from the
  // session it just killed. This is what stops a LATE finalize — e.g. the web
  // handle's 3s stop() fallback, or a native recogniser lagging past stop() —
  // from firing onFinal/onEnd (and auto-sending the coach's own tail) after
  // playback or a manual replay has already taken over. The happy path (silence
  // timer → handle.stop() → onEnd) never calls the hook's stop(), so its gen is
  // unchanged and the auto-send still runs.
  const genRef = useRef(0);

  // Keep the latest callbacks without re-creating start/stop each render.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });

  // Keep the mic warm while voice mode is on, and release it when off/unmounting
  // so the mic indicator doesn't linger. This is what makes the SECOND+ turn's
  // capture start instantly (no getUserMedia/permission round-trip per turn).
  useEffect(() => {
    if (!STT_SUPPORTED || !opts.warm) return;
    onSttPrepareProgress(setPreparingProgress);
    void prewarmStt();
    return () => {
      onSttPrepareProgress(null);
      releaseStt();
    };
  }, [opts.warm]);

  // Capture is truly live: safe to prompt "speak now". Idempotent — fires from
  // the engine's onStart, or defensively from the first transcript if an engine
  // doesn't emit one.
  const markCapturing = useCallback(() => {
    if (!captureStartedAtRef.current) captureStartedAtRef.current = Date.now();
    // MEASUREMENT ONLY: how long the mic took to actually open, split by
    // whether this is the session's first turn. Native and web are slow for
    // different reasons — native pays a permission check, an idle poll and an
    // OS recogniser bind on EVERY turn; web can be waiting on the whisper
    // model, the worker and the audio graph, overwhelmingly on turn one. One
    // number, two stories, and until now neither was recorded:
    // `features/voice/metrics.ts` declares the latency metrics and nothing has
    // ever called `recordLatency`.
    if (startRequestedAtRef.current) {
      probe("voice.mic.live", {
        ms: Date.now() - startRequestedAtRef.current,
        firstTurn: micTurnsRef.current === 0 ? 1 : 0,
      });
      micTurnsRef.current += 1;
      startRequestedAtRef.current = 0;
    }
    startedRef.current = true;
    setPreparing(false);
    setListening(true);
  }, []);

  // Record the first captured word's latency once per session (mic-live → first
  // word). Consistently tiny values across turns hint users were queued up to
  // keep talking — a signal we'd been cutting them off.
  const noteFirstWord = useCallback(() => {
    if (firstCapturedAtRef.current) return;
    const now = Date.now();
    firstCapturedAtRef.current = now;
    if (captureStartedAtRef.current) {
      endpointMetrics.recordFirstWord(now - captureStartedAtRef.current);
    }
  }, []);

  const clearSilence = useCallback(() => {
    if (silenceRef.current) {
      clearTimeout(silenceRef.current);
      silenceRef.current = null;
    }
  }, []);

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPendingSend(false);
  }, []);

  const stop = useCallback(() => {
    genRef.current++; // invalidate any in-flight callbacks from the current session
    clearSilence();
    handleRef.current?.stop();
    handleRef.current = null;
    cancelPending();
    setListening(false);
    setPreparing(false);
    setLevel(0);
  }, [cancelPending, clearSilence]);

  // Acoustic end-of-utterance decision (native). End the turn only once we've
  // captured speech AND the room has been quiet for `quietMs` AND the transcript
  // has stopped changing for `transcriptStableMs` — so live audio (or still-
  // arriving words) holds the mic open through the recogniser's result lag,
  // which is exactly what a laggy time-since-last-result timer got wrong. A
  // `maxSilenceMs` cap ends it anyway if the transcript goes idle while the mic
  // still reads as "voiced" (steady background noise). Runs on each volume tick.
  const evaluateEndpoint = useCallback(() => {
    if (STT_OWNS_ENDPOINTING) return; // web worker VAD ends the turn itself
    if (endingRef.current) return; // already ended this turn — await onEnd
    if (!capturedRef.current) return; // nothing captured yet — never end early
    const now = Date.now();
    const quietFor = now - lastVoiceAtRef.current;
    const stableFor = now - lastTextChangeAtRef.current;
    const done =
      (quietFor >= ENDPOINT.quietMs && stableFor >= ENDPOINT.transcriptStableMs) ||
      stableFor >= ENDPOINT.maxSilenceMs;
    if (done) {
      endingRef.current = true;
      endReasonRef.current =
        quietFor >= ENDPOINT.quietMs && stableFor >= ENDPOINT.transcriptStableMs
          ? "acoustic"
          : "maxcap";
      endTrailingSilenceRef.current = quietFor;
      clearSilence();
      handleRef.current?.stop(); // finalizes → onEnd → auto-send
    }
  }, [clearSilence]);

  // Fallback end-of-utterance timer: a plain time-since-last-result countdown.
  // Only governs when the acoustic detector can't — the engine owns endpointing
  // (web), or no `volumechange` audio has arrived yet on this device. Once real
  // levels flow, the acoustic detector takes over and this bails.
  const armSilence = useCallback(() => {
    if (STT_OWNS_ENDPOINTING) return;
    const ms = optsRef.current.silenceMs;
    if (!ms) return;
    clearSilence();
    silenceRef.current = setTimeout(() => {
      silenceRef.current = null;
      if (haveLevelsRef.current) return; // acoustic detector owns endpointing now
      endReasonRef.current = "fallback";
      endTrailingSilenceRef.current = ms;
      handleRef.current?.stop();
    }, ms);
  }, [clearSilence]);

  const start = useCallback(() => {
    if (!STT_SUPPORTED || handleRef.current) return;
    // Hard gate: NEVER open the mic while ANY coach audio is playing — auto-play
    // OR the manual Play button — otherwise it captures the coach's own voice and
    // auto-sends it (the "reading my messages back" bug). This blocks every
    // restart path (relisten timer, resume, stray effect) for the whole clip; the
    // mic resumes only once playback ends. (Barge-in still works: it stops the
    // clip first, so playback is no longer active by the time it starts the mic.)
    if (isCoachPlaying() || isManualReplayActive() || isUserTyping()) return;
    cancelPending();
    capturedRef.current = false;
    latestTextRef.current = "";
    // Fresh endpointing state for this session. Seed the activity clocks to now
    // so we never end before the first word / audio arrives.
    haveLevelsRef.current = false;
    noiseFloorRef.current = 0;
    lastVoiceAtRef.current = Date.now();
    lastTextChangeAtRef.current = Date.now();
    endingRef.current = false;
    startedRef.current = false;
    captureStartedAtRef.current = 0;
    firstCapturedAtRef.current = 0;
    endReasonRef.current = null;
    endTrailingSilenceRef.current = 0;
    // This session's fence token. Every callback below drops out if stop() (i.e.
    // playback/replay took over) has since bumped the generation.
    const myGen = ++genRef.current;
    const isStale = () => myGen !== genRef.current;
    setNotice(null);
    // Not "listening" yet — only capturing after the engine's onStart fires.
    startRequestedAtRef.current = Date.now();
    setPreparing(true);
    setListening(false);
    // Belt-and-braces: discard any transcript that arrives while coach audio is
    // playing. On native the system recogniser can lag on stop() and still emit a
    // final result of the coach's own voice; dropping it here guarantees the
    // "reading my messages back" capture can't happen even if the recogniser
    // keeps running a beat too long. On web the mic is already gated off, so this
    // is simply redundant there.
    const coachAudioActive = () =>
      isCoachPlaying() || isManualReplayActive() || isUserTyping();
    // Interim and final results are handled identically: the engine reports the
    // whole turn so far either way (native accumulates its segments in stt),
    // so a final is just a more confident snapshot of the same turn — NOT a turn
    // boundary. Only the hook's endpointing decides when a turn is over.
    const onTranscript = (t: string) => {
      if (isStale() || coachAudioActive()) return;
      const trimmed = t.trim();
      if (trimmed) {
        // Only NEW words count as proof of continued speech. The engine re-emits
        // the unchanged running total during a pause, and letting that reset the
        // quiet clock would hold the mic open forever.
        if (trimmed !== latestTextRef.current) {
          const now = Date.now();
          lastTextChangeAtRef.current = now;
          lastVoiceAtRef.current = now;
          latestTextRef.current = trimmed;
          armSilence();
        }
        capturedRef.current = true;
        markCapturing();
        noteFirstWord();
      }
      optsRef.current.onText(t);
    };
    const handle = startStt({
      onStart: () => {
        if (isStale()) return;
        markCapturing();
      },
      onLevel: (rms) => {
        if (isStale() || coachAudioActive()) return;
        const now = Date.now();
        if (now - lastLevelAt.current >= 80) {
          lastLevelAt.current = now;
          setLevel(rms);
        }
        // Acoustic VAD for native endpointing. Real audio above the adaptive
        // floor holds the mic open (resets the quiet clock); quiet samples
        // adapt the floor to the room. Then re-check the end-of-turn condition.
        if (!STT_OWNS_ENDPOINTING) {
          haveLevelsRef.current = true;
          const floor = noiseFloorRef.current;
          const threshold = Math.max(ENDPOINT.absVoice, floor + ENDPOINT.voiceMargin);
          if (rms >= threshold) {
            lastVoiceAtRef.current = now;
          } else {
            noiseFloorRef.current = floor === 0 ? rms : floor * 0.9 + rms * 0.1;
          }
          evaluateEndpoint();
        }
      },
      onInterim: onTranscript,
      onFinal: onTranscript,
      onEnd: () => {
        // Stale = stop() already took over (playback/replay). Drop the whole
        // finalize so a late tail can't auto-send or resume listening.
        if (isStale()) return;
        // Close this session's fence from the inside. The engine layer already
        // collapses error+end into one callback, but a late straggler from a
        // recogniser that outlived its own teardown would otherwise still be
        // "current" and could report a second ending — scheduling a second
        // restart, which is how one failure becomes a loop.
        genRef.current++;
        clearSilence();
        setListening(false);
        setPreparing(false);
        setLevel(0);
        handleRef.current = null;
        const captured = capturedRef.current;
        optsRef.current.onSessionEnd?.({
          captured,
          started: startedRef.current,
        });
        // Recognition ended with nothing captured (immediate error / silence
        // timeout): do not auto-send whatever was already in the composer.
        if (!captured) {
          setPendingSend(false);
          return;
        }
        // "Review each turn": leave the transcript for manual edit/send.
        if (optsRef.current.reviewMode) {
          setPendingSend(false);
          return;
        }
        // Endpoint-quality telemetry: count this as a submitted turn only when an
        // AUTOMATIC endpoint drove it (acoustic / cap / fallback), so the
        // false-endpoint measure ignores manual stops. See endpoint-metrics.
        if (endReasonRef.current) {
          const startedAt = captureStartedAtRef.current || Date.now();
          endpointMetrics.recordTurn({
            reason: endReasonRef.current,
            trailingSilenceMs: endTrailingSilenceRef.current,
            utteranceMs: Date.now() - startedAt,
          });
          logEndpointSnapshot();
        }
        // Auto-send. When silenceMs is set the pause already happened, so send
        // after only a brief settle for the final transcript; otherwise use the
        // longer review window.
        setPendingSend(true);
        const delay = optsRef.current.silenceMs
          ? (optsRef.current.reviewMs ?? 300)
          : (optsRef.current.reviewMs ?? 1500);
        timerRef.current = setTimeout(() => {
          setPendingSend(false);
          timerRef.current = null;
          optsRef.current.onSend(latestTextRef.current);
        }, delay);
      },
      onError: (message) => {
        if (isStale()) return;
        genRef.current++; // as in onEnd: exactly one ending per session
        clearSilence();
        setListening(false);
        setPreparing(false);
        setLevel(0);
        handleRef.current = null;
        setPendingSend(false);
        setNotice(noticeForError(message));
        optsRef.current.onSessionEnd?.({
          captured: false,
          started: startedRef.current,
          errorCode: message,
        });
      },
    });
    if (!handle) {
      setListening(false);
      setPreparing(false);
      return;
    }
    handleRef.current = handle;
  }, [
    cancelPending,
    clearSilence,
    armSilence,
    evaluateEndpoint,
    markCapturing,
    noteFirstWord,
  ]);

  useEffect(() => stop, [stop]);

  // Disable listening for the whole duration of ANY coach audio. Stop the mic the
  // instant a clip starts — auto-play (subscribePlayback) or the manual Play
  // button (subscribeManualReplay, which also covers the fetch window before the
  // clip actually starts). `start()` is gated on the same conditions, so no
  // restart path can reopen the mic until playback ends.
  useEffect(() => subscribePlayback((playing) => {
    if (playing) stop();
  }), [stop]);
  useEffect(() => subscribeManualReplay((replaying) => {
    if (replaying) stop();
  }), [stop]);
  // The user started typing — pause the mic so keystroke clatter isn't captured
  // and auto-sent, and so a resumed mic can't overwrite their half-typed text.
  useEffect(() => subscribeTyping((typing) => {
    if (typing) stop();
  }), [stop]);

  return {
    supported,
    label: STT_LABEL,
    listening,
    preparing,
    preparingProgress,
    level,
    pendingSend,
    notice,
    start,
    stop,
    cancelPending,
    showNotice: setNotice,
  };
}
